// noinspection JSUnusedGlobalSymbols

import { type ImagePickerAsset } from 'expo-image-picker'

import { IMAGE_WIDTH_MAX } from '@/lib'

export const normalizeImage = async (asset: ImagePickerAsset) => {
  const width = Math.min(asset.width, IMAGE_WIDTH_MAX)
  const height = asset.height * (width / asset.width)

  // Determine if we need to crop to square
  const needsCropping = height !== width
  const cropSize = needsCropping ? Math.min(width, height) : undefined

  return new Promise<Blob>((resolve, reject) => {
    const img = new Image()

    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Set canvas dimensions
        if (cropSize) {
          canvas.width = cropSize
          canvas.height = cropSize
        } else {
          canvas.width = width
          canvas.height = height
        }

        // Calculate crop dimensions for center-crop
        let sourceX = 0
        let sourceY = 0
        let sourceWidth = img.width
        let sourceHeight = img.height

        if (cropSize) {
          // Center-crop to square
          const size = Math.min(img.width, img.height)
          sourceX = Math.floor((img.width - size) / 2)
          sourceY = Math.floor((img.height - size) / 2)
          sourceWidth = size
          sourceHeight = size
        }

        // Clear canvas before drawing
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // Draw the image with transformations
        ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, canvas.width, canvas.height)

        // Convert to blob with error handling
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob)
            } else {
              reject(new Error('Failed to create blob from canvas'))
            }
          },
          'image/webp',
          0.9
        )
      } catch (error) {
        reject(new Error(`Canvas manipulation failed: ${error instanceof Error ? error.message : 'Unknown error'}`))
      }
    }

    img.onerror = () => reject(new Error('Failed to load image for manipulation'))
    img.src = asset.uri
  })
}
